import React, { memo } from 'react';
import styled from 'styled-components';
import Skeleton from 'react-loading-skeleton';
import { v4 as uuidv4 } from 'uuid';
import is from 'styled-is';
import { random } from 'lodash';
import { largeUp, xLargeUp } from '@honeybook/hbui/dist/styles/breakpoints';
import { TCardSize } from '../../../CreateFlow/types/create-flow.types';
import { Cards } from '../CardsContainer/CardsContainer.styles';
import { useWindowSize } from '@honeybook/hbui/dist/components/common/useWindowSize';
import { TEMPLATE_GALLERY_CARD_STYLES } from '~/components/common/FlowTemplates/FlowGalleryCard/FlowGalleryCards.styles';

interface Props {
    count?: number;
    size?: TCardSize;
    sidebar?: boolean;
    isNoContainer?: boolean;
    shouldShowRows?: boolean;
    isFlowGallery?: boolean;
    rowsHeight?: number | string;
}

const Shimmer = ({
    count = 3,
    size = 'md',
    sidebar = false,
    isNoContainer = false,
    shouldShowRows = false,
    isFlowGallery = false,
    rowsHeight = 65
}: Props) => {
    const list = Array.from(Array(count).keys());

    const { isMediumUp } = useWindowSize();

    if (sidebar) {
        return (
            <>
                {list.map(item => (
                    <MenuListItem key={item}>
                        <Skeleton height={20} />
                    </MenuListItem>
                ))}
            </>
        );
    }

    const { size: cardSize } = TEMPLATE_GALLERY_CARD_STYLES;

    const randomPercent = (min, max) => `${random(min, max)}%`;

    const randomPixel = (min, max) => `${random(min, max)}px`;

    const largeList = (
        <>
            {list.map(item => (
                <Container key={item}>
                    <Card
                        height={
                            isMediumUp ? cardSize.md.height : cardSize.sm.height
                        }
                    />
                    <Title height={19} width={randomPercent(20, 40)} />
                    <FirstTextLine height={12} width="95%" />
                    <SecondTextLine height={12} width={randomPercent(20, 60)} />
                    <TagsContainer>
                        {[...Array(random(1, 4))].map(() => (
                            <Tag
                                key={uuidv4()}
                                height={12}
                                width={randomPixel(55, 65)}
                            />
                        ))}
                    </TagsContainer>
                </Container>
            ))}
        </>
    );

    if (shouldShowRows) {
        const rowList = list.map(item => (
            <Skeleton key={item} height={rowsHeight} />
        ));

        return isNoContainer ? (
            <>{rowList}</>
        ) : (
            <RowsContainer>{rowList}</RowsContainer>
        );
    }

    if (size === 'lg') {
        return isNoContainer ? (
            <>{largeList}</>
        ) : (
            <StyledCardsLarge isFlowGallery={isFlowGallery} size="lg">
                {largeList}
            </StyledCardsLarge>
        );
    }

    const mediumList = list.map(item => <Skeleton key={item} height={190} />);

    return isNoContainer ? (
        <>{mediumList}</>
    ) : (
        <StyledCardsMedium size="md">{mediumList}</StyledCardsMedium>
    );
};

export default memo(Shimmer);

/* styles */
const MenuListItem = styled.div`
    margin-bottom: 8px;
`;

const Container = styled.div`
    font-size: 0;
    display: flex;
    flex-direction: column;
`;

const Card = styled(Skeleton)`
    margin-bottom: 24px;
`;

const Title = styled(Skeleton)`
    margin-bottom: 10px;
`;

const FirstTextLine = styled(Skeleton)`
    margin-bottom: 6px;
`;

const SecondTextLine = styled(Skeleton)`
    margin-bottom: 16px;
`;

const TagsContainer = styled.div`
    width: 100%;
    display: flex;
    gap: 16px;
    margin-bottom: 2px;
`;

const Tag = styled(Skeleton)``;

const RowsContainer = styled.div`
    display: flex;
    flex-direction: column;
`;

const StyledCardsMedium = styled(Cards)`
    gap: 32px;
    ${xLargeUp} {
        justify-content: center;
    }
`;

const StyledCardsLarge = styled(Cards)<{ isFlowGallery?: boolean }>`
    ${is('isFlowGallery')`
    grid-template-columns: minmax(0, 1fr);
    margin: 0 auto;
    
    ${largeUp} {
        grid-template-columns: repeat(2, minmax(340px, 440px));
        gap: 32px;
    }
    `}
`;
